import React from 'react';
import { Box, Chip, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AccessTime, LocationOn } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';
import TimeUtils from '../../../utils/TimeUtils';
import { useStartingPriceLabel } from '../../common/storefrontPriceUtils';

interface ExperienceHeaderProps {
  name: string;
  locationId?: string;
  durationMinutes: number;
  orderPage: OrderPageData;
  account: AccountData;
  isTrial?: boolean;
}

export const ExperienceHeader = ({
  name,
  locationId,
  durationMinutes,
  orderPage,
  account,
  isTrial,
}: ExperienceHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();

  const location = account.locations?.find((loc) => loc.id === locationId);
  const locationName = location?.name?.[language] || location?.name?.en;
  const { label: priceLabel, startingPrice } = useStartingPriceLabel(orderPage, account);

  return (
    <Box sx={{ marginBottom: isSmallScreen ? 3 : 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          marginBottom: 2,
        }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: isSmallScreen ? '1.75rem' : '2.25rem',
            lineHeight: 1.2,
          }}>
          {name}
        </Typography>
        {isTrial && (
          <Chip
            label={t('storefront.trialSessions')}
            size="small"
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      <Grid container gap={isSmallScreen ? 2 : 3} alignItems="center" flexWrap="wrap">
        {locationName && (
          <Grid container alignItems="center" gap={0.5} width="auto">
            <LocationOn sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="body1" color="text.secondary">
              {locationName}
            </Typography>
          </Grid>
        )}

        <Grid container alignItems="center" gap={0.5} width="auto">
          <AccessTime sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body1" color="text.secondary">
            {TimeUtils.formatDuration(durationMinutes, t)}
          </Typography>
        </Grid>

        {startingPrice > 0 && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}>
            {priceLabel}
          </Typography>
        )}
      </Grid>
    </Box>
  );
};
